import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronLeft } from 'lucide-react';
import { useOrganization } from '../contexts/OrganizationContext';

// Organization and residence data
const ORGANIZATIONS = [
    {
        id: 'elysian',
        name: 'Elysian Residences',
        tagline: 'Later living, reimagined',
        hasResidences: true,
        residences: [
            { id: 'oakleigh', name: 'The Oakleigh', location: 'St Albans' },
            { id: 'watermark', name: 'The Watermark', location: 'Kew' },
            { id: 'bower', name: 'The Bower', location: 'Henley-on-Thames' },
            { id: 'oren', name: 'The Oren', location: 'Hampstead' },
            { id: 'denton', name: 'The Denton', location: 'Berkhamsted' },
            { id: 'beckett', name: 'The Beckett', location: 'Royal Tunbridge Wells' },
            { id: 'landsby', name: 'Landsby', location: 'Stanmore' },
            { id: 'wildernesse', name: 'Wildernesse House', location: 'Sevenoaks' },
        ]
    },
    {
        id: 'burston',
        name: 'Burston Garden Centre',
        tagline: 'Growing inspiration since 1978',
        hasResidences: false
    }
];

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1,
            delayChildren: 0.2
        }
    },
    exit: {
        opacity: 0,
        x: -20,
        transition: { duration: 0.3 }
    }
};

const itemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }
    }
};

const pageVariants = {
    initial: { opacity: 0, x: 30 },
    animate: { opacity: 1, x: 0, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] } },
    exit: { opacity: 0, x: -30, transition: { duration: 0.3 } }
};

export function CanopyGatewayPage() {
    const navigate = useNavigate();
    const { organization, loading, isSubdomainOrg } = useOrganization();
    const [step, setStep] = useState('organizations'); // 'organizations' | 'residences' | 'welcome'
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [selectedResidence, setSelectedResidence] = useState(null);
    const [hoveredItem, setHoveredItem] = useState(null);

    // Auto-redirect if org is loaded from subdomain
    useEffect(() => {
        // Check if we have a valid org loaded from subdomain (slug is not 'default')
        if (!loading && isSubdomainOrg && organization?.slug && organization.slug !== 'default') {
            // Find matching org from our list to check if it has residences
            const matchingOrg = ORGANIZATIONS.find(o => 
                o.id === organization.id || 
                o.id === organization.slug ||
                o.name.toLowerCase().includes(organization.slug?.toLowerCase() || '')
            );
            
            if (matchingOrg) {
                if (matchingOrg.hasResidences) {
                    // Show residence selection for this org
                    setSelectedOrg(matchingOrg);
                    setStep('residences');
                } else {
                    // No residences - redirect directly to login
                    localStorage.setItem('selectedOrg', JSON.stringify({ 
                        id: organization.id || organization.slug, 
                        name: organization.name,
                        residence: null 
                    }));
                    navigate('/login');
                }
            } else {
                // Org not in our list but exists in backend - redirect to login
                localStorage.setItem('selectedOrg', JSON.stringify({ 
                    id: organization.id || organization.slug, 
                    name: organization.name,
                    residence: null 
                }));
                navigate('/login');
            }
        }
    }, [loading, isSubdomainOrg, organization, navigate]);

    // Show loading state while checking subdomain
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F9F8F6' }}>
                <div className="text-center">
                    <img 
                        src="/canopy-logo.png"
                        alt="Canopy"
                        className="h-12 mx-auto mb-4"
                    />
                    <div className="w-8 h-8 border-2 border-[#8A9A86] border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
            </div>
        );
    }

    const handleSelectOrganization = (org) => {
        setSelectedOrg(org);
        if (org.hasResidences) {
            setStep('residences');
        } else {
            // Go directly to login for orgs without residences
            localStorage.setItem('selectedOrg', JSON.stringify({ 
                id: org.id, 
                name: org.name,
                residence: null 
            }));
            navigate('/login');
        }
    };

    const handleSelectResidence = (residence) => {
        setSelectedResidence(residence);
        localStorage.setItem('selectedOrg', JSON.stringify({ 
            id: selectedOrg.id, 
            name: selectedOrg.name,
            residence: residence 
        }));
        navigate('/login');
    };

    const handleBack = () => {
        if (step === 'residences') {
            setStep('organizations');
            setSelectedOrg(null);
        }
    };

    return (
        <div 
            className="min-h-screen flex flex-col lg:flex-row"
            style={{ 
                backgroundColor: '#F9F8F6',
                fontFamily: "'Outfit', sans-serif"
            }}
            data-testid="canopy-gateway"
        >
            {/* Left: Focal Image */}
            <div className="lg:w-1/2 h-[40vh] lg:h-screen relative overflow-hidden">
                <motion.img
                    src="/gateway-hero.jpg"
                    alt="Elysian Residences community"
                    className="w-full h-full object-cover"
                    initial={{ scale: 1.1, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent" />
                
                {/* Brand Mark - Floating */}
                <motion.div 
                    className="absolute top-8 left-8 lg:top-12 lg:left-12"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.6 }}
                >
                    <img 
                        src="/canopy-logo.png"
                        alt="Canopy"
                        className="h-16 lg:h-20 w-auto object-contain drop-shadow-lg rounded-lg"
                    />
                </motion.div>
            </div>

            {/* Right: Directory Content */}
            <div className="lg:w-1/2 flex flex-col justify-center px-8 py-12 lg:px-16 lg:py-0 min-h-[60vh] lg:min-h-screen">
                <div className="max-w-md mx-auto lg:mx-0 w-full">
                    <AnimatePresence mode="wait">
                        {/* Organizations List */}
                        {step === 'organizations' && (
                            <motion.div
                                key="organizations"
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                            >
                                <motion.p 
                                    className="text-xs tracking-[0.2em] uppercase mb-8 lg:mb-12"
                                    style={{ color: '#787C7A' }}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.3 }}
                                >
                                    Select your community
                                </motion.p>

                                <motion.nav
                                    variants={containerVariants}
                                    initial="hidden"
                                    animate="visible"
                                    className="space-y-6 lg:space-y-8"
                                >
                                    {ORGANIZATIONS.map((org) => (
                                        <motion.button
                                            key={org.id}
                                            variants={itemVariants}
                                            onClick={() => handleSelectOrganization(org)}
                                            onMouseEnter={() => setHoveredItem(org.id)}
                                            onMouseLeave={() => setHoveredItem(null)}
                                            className="group w-full text-left flex items-center justify-between py-3 border-b transition-all duration-300"
                                            style={{ 
                                                borderColor: hoveredItem === org.id ? '#8A9A86' : '#E5E4E2',
                                            }}
                                            data-testid={`org-${org.id}`}
                                        >
                                            <div>
                                                <h2 
                                                    className="text-2xl lg:text-3xl transition-all duration-300"
                                                    style={{ 
                                                        fontFamily: "'Cormorant Garamond', serif",
                                                        fontWeight: 300,
                                                        color: hoveredItem === org.id ? '#2C302E' : '#787C7A',
                                                        transform: hoveredItem === org.id ? 'translateX(8px)' : 'translateX(0)'
                                                    }}
                                                >
                                                    {org.name}
                                                </h2>
                                                <p 
                                                    className="text-sm mt-1 transition-opacity duration-300"
                                                    style={{ 
                                                        color: '#787C7A',
                                                        opacity: hoveredItem === org.id ? 1 : 0.6
                                                    }}
                                                >
                                                    {org.tagline}
                                                </p>
                                            </div>
                                            <ArrowRight 
                                                className="w-5 h-5 transition-all duration-300"
                                                style={{ 
                                                    color: '#8A9A86',
                                                    opacity: hoveredItem === org.id ? 1 : 0,
                                                    transform: hoveredItem === org.id ? 'translateX(0)' : 'translateX(-10px)'
                                                }}
                                            />
                                        </motion.button>
                                    ))}
                                </motion.nav>

                                <motion.p 
                                    className="mt-12 lg:mt-16 text-sm"
                                    style={{ color: '#787C7A' }}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 0.7 }}
                                    transition={{ delay: 0.8 }}
                                >
                                    A curated network of places and communities
                                </motion.p>
                            </motion.div>
                        )}

                        {/* Residences List */}
                        {step === 'residences' && selectedOrg && (
                            <motion.div
                                key="residences"
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                            >
                                <motion.button
                                    onClick={handleBack}
                                    className="flex items-center gap-2 text-sm mb-8 transition-colors duration-300 hover:opacity-70"
                                    style={{ color: '#787C7A' }}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    data-testid="back-btn"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    Back to communities
                                </motion.button>

                                <motion.div className="mb-8 lg:mb-12">
                                    <h2 
                                        className="text-3xl lg:text-4xl mb-2"
                                        style={{ 
                                            fontFamily: "'Cormorant Garamond', serif",
                                            fontWeight: 300,
                                            color: '#2C302E'
                                        }}
                                    >
                                        {selectedOrg.name}
                                    </h2>
                                    <p 
                                        className="text-xs tracking-[0.2em] uppercase"
                                        style={{ color: '#787C7A' }}
                                    >
                                        Select your residence
                                    </p>
                                </motion.div>

                                <motion.nav
                                    variants={containerVariants}
                                    initial="hidden"
                                    animate="visible"
                                    className="space-y-4 lg:space-y-5 max-h-[50vh] overflow-y-auto pr-4"
                                    style={{ scrollbarWidth: 'thin' }}
                                >
                                    {selectedOrg.residences.map((residence) => (
                                        <motion.button
                                            key={residence.id}
                                            variants={itemVariants}
                                            onClick={() => handleSelectResidence(residence)}
                                            onMouseEnter={() => setHoveredItem(residence.id)}
                                            onMouseLeave={() => setHoveredItem(null)}
                                            className="group w-full text-left flex items-center justify-between py-2 transition-all duration-300"
                                            data-testid={`residence-${residence.id}`}
                                        >
                                            <div>
                                                <h3 
                                                    className="text-xl lg:text-2xl transition-all duration-300"
                                                    style={{ 
                                                        fontFamily: "'Cormorant Garamond', serif",
                                                        fontWeight: 400,
                                                        color: hoveredItem === residence.id ? '#2C302E' : '#787C7A',
                                                        transform: hoveredItem === residence.id ? 'translateX(8px)' : 'translateX(0)'
                                                    }}
                                                >
                                                    {residence.name}
                                                </h3>
                                                <p 
                                                    className="text-xs tracking-wider uppercase mt-0.5 transition-opacity duration-300"
                                                    style={{ 
                                                        color: '#8A9A86',
                                                        opacity: hoveredItem === residence.id ? 1 : 0.5
                                                    }}
                                                >
                                                    {residence.location}
                                                </p>
                                            </div>
                                            <ArrowRight 
                                                className="w-4 h-4 transition-all duration-300"
                                                style={{ 
                                                    color: '#8A9A86',
                                                    opacity: hoveredItem === residence.id ? 1 : 0,
                                                    transform: hoveredItem === residence.id ? 'translateX(0)' : 'translateX(-10px)'
                                                }}
                                            />
                                        </motion.button>
                                    ))}
                                </motion.nav>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
}

export default CanopyGatewayPage;
